import { useEffect, useMemo, useState } from 'react';
import { useBodyClass } from '@/hooks/useBodyClass';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Check, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTelegram } from '@/hooks/useTelegram';
import { mealTypeLabel, fmtNum } from '@/utils/format';
import type { Partner } from '@/hooks/usePartners';
import type { Meal, MealType } from '@/types';

/**
 * Отправка партнёру: выбрать продукты галочками и отправить разом (10.10.2026).
 *
 * Боевой случай: жена записала завтрак несколькими записями и хотела отправить
 * его Славе — кнопка «Отправить» была у каждой записи своя, и завтрак пришлось бы
 * слать по одному продукту. Слава: «дать возможность до отправки выбрать несколько
 * продуктов и подтвердить». Отдельной кнопки «весь завтрак» нет намеренно (его же
 * решение): окно открывается с отмеченным, лишнее снимается одним тапом.
 *
 * У получателя выбранное появляется ОДНИМ приёмом того же типа (миграция 062).
 */
export function SendToPartnerSheet({
  open, onClose, mealType, meals, partners, preselectMealId,
}: {
  open: boolean;
  onClose: () => void;
  mealType: MealType;
  /** Все записи этого приёма за день */
  meals: Meal[];
  partners: Partner[];
  /** Открыли из конкретной записи — отмечаем только её продукты; иначе всё */
  preselectMealId?: string | null;
}) {
  // Экраны лежат в слое ниже нижней панели (z-10 против z-30): без этого панель
  // закрывает низ выезжающего окна вместе с его кнопками (боевой случай 10.10.2026).
  useBodyClass('modal-open', open);
  const { haptic } = useTelegram();
  const items = useMemo(
    () => meals.flatMap((m) => (m.items ?? []).map((i: any) => ({ ...i, mealId: m.id }))),
    [meals],
  );
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [to, setTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<'sent' | 'auto' | 'error' | null>(null);

  useEffect(() => {
    if (!open) return;
    setChecked(new Set(items.filter((i) => !preselectMealId || i.mealId === preselectMealId).map((i) => i.id)));
    setTo(partners[0]?.id ?? null);
    setResult(null);
  }, [open]);

  const picked = items.filter((i) => checked.has(i.id));
  const kcal = picked.reduce((s, i) => s + (Number(i.calories) || 0), 0);
  const partner = partners.find((p) => p.id === to) ?? null;
  const allOn = picked.length === items.length;

  function toggle(id: string) {
    haptic('light');
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function send() {
    if (!to || !picked.length || busy) return;
    setBusy(true);
    haptic('medium');
    try {
      const { data, error } = await supabase.rpc('send_items_to_partner', {
        p_item_ids: picked.map((i) => i.id),
        p_to: to,
        p_meal_type: mealType,
      });
      if (error || !data?.ok) throw error ?? new Error(data?.error);
      haptic('success');
      setResult(data.auto ? 'auto' : 'sent');
      setTimeout(onClose, 1800);
    } catch (e) {
      console.error('[send-items-to-partner]', e);
      haptic('error');
      setResult('error');
    } finally {
      setBusy(false);
    }
  }

  const word = (n: number) => (n % 10 === 1 && n % 100 !== 11 ? 'продукт' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'продукта' : 'продуктов');

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 flex flex-col bg-bg-card rounded-t-3xl border-t border-line max-w-md mx-auto"
            style={{ maxHeight: '85dvh' }}
          >
            <div className="px-5 py-3 flex items-center justify-between border-b border-line/40 shrink-0">
              <div>
                <div className="font-display font-bold text-base leading-tight">
                  {/* Имя в именительном: «→ Надя», а не «отправить Надя» */}
                  {mealTypeLabel[mealType]} → {partner?.name || 'партнёру'}
                </div>
                <div className="text-xs text-ink-dim">Отметь, что отправить. Придёт одним приёмом</div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-bg-elevated text-ink-muted flex items-center justify-center shrink-0"
                aria-label="Закрыть"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-5 pt-3 overflow-y-auto min-h-0 flex-1">
              {partners.length > 1 && (
                <div className="flex gap-2 mb-3">
                  {partners.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setTo(p.id); haptic('light'); }}
                      className={`flex-1 py-2 rounded-xl border text-xs transition-colors ${
                        to === p.id ? 'bg-sky-glow/15 border-sky-glow/50 text-ink' : 'bg-bg-elevated border-line text-ink-muted'
                      }`}
                    >
                      {p.name || 'Партнёр'}
                    </button>
                  ))}
                </div>
              )}

              <button
                onClick={() => { haptic('light'); setChecked(allOn ? new Set() : new Set(items.map((i) => i.id))); }}
                className="text-[11px] text-sky-glow mb-2 active:opacity-70"
              >
                {allOn ? 'Снять все' : 'Выбрать все'}
              </button>

              <div className="space-y-1.5 pb-3">
                {items.map((i) => {
                  const on = checked.has(i.id);
                  return (
                    <button
                      key={i.id}
                      onClick={() => toggle(i.id)}
                      className={`w-full p-2.5 rounded-xl border flex items-center gap-3 text-left transition-colors ${
                        on ? 'bg-sky-glow/10 border-sky-glow/40' : 'bg-bg-elevated/50 border-line'
                      }`}
                    >
                      <span
                        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                          on ? 'bg-sky-glow border-sky-glow text-bg' : 'border-line'
                        }`}
                      >
                        {on && <Check size={13} strokeWidth={3} />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className={`block text-sm truncate ${on ? '' : 'text-ink-muted'}`}>{i.name}</span>
                        <span className="block text-[11px] text-ink-dim">
                          {fmtNum(Number(i.weight_g) || 0)} г · {fmtNum(Number(i.calories) || 0)} ккал
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="px-5 pt-3 border-t border-line/40 shrink-0" style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}>
              {result === 'error' && (
                <p className="text-xs text-danger text-center mb-2">Не получилось отправить. Попробуй ещё раз.</p>
              )}
              <button
                onClick={send}
                disabled={!picked.length || !to || busy || result === 'sent' || result === 'auto'}
                className="w-full py-3 rounded-xl bg-sky-glow text-bg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform"
              >
                {busy ? <Loader2 size={16} className="animate-spin" />
                  : result === 'sent' || result === 'auto' ? <Check size={16} /> : <Send size={16} />}
                {result === 'auto'
                  ? `${partner?.name || 'Партнёр'}: уже в дневнике`
                  : result === 'sent'
                    ? `Отправлено — ${partner?.name || 'партнёр'} подтвердит у себя`
                    : picked.length
                      ? `Отправить ${picked.length} ${word(picked.length)} · ${fmtNum(kcal)} ккал`
                      : 'Отметь хотя бы один продукт'}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
